import { useGlobalState } from '../state/useGlobalState';
import { useStore, useActions } from './useStore';
import { useStableCallback } from './hookUtils';
import type { StoreState } from '../types/types';

/**
 * A hook to manage persistent state (filters, sorting) for a specific panel.
 *
 * ADR-003 compliant: Uses useStableCallback to ensure the setPanelState function
 * maintains a stable reference across renders. This prevents unnecessary re-renders
 * in child components that receive this callback as a prop or use it in dependency arrays.
 *
 * @param panelId A unique identifier for the panel (e.g., 'npcPanel', 'lorePanel').
 * @returns An object with the current state and a function to update it.
 */
export const usePanelState = (panelId: string) => {
  const panelSettings = useStore((state: StoreState) => state.settings.panelSettings?.[panelId]);
  const { updateSettings } = useActions();

  // ADR-003: Use useStableCallback to maintain a stable reference for the setter function.
  // This prevents unnecessary re-renders when this callback is passed as a prop to child
  // components or used in useEffect dependency arrays. The callback always has access to
  // the latest panelId and updateSettings values through the closure.
  const setPanelState = useStableCallback((newState: { filters?: Record<string, unknown>; sort?: string }) => {
    const currentSettings = useGlobalState.getState().settings.panelSettings ?? {};
    updateSettings({
      panelSettings: {
        ...currentSettings,
        [panelId]: {
          ...(currentSettings[panelId] ?? {}),
          ...newState,
        },
      },
    });
  });

  return {
    filters: panelSettings?.filters ?? {},
    sort: panelSettings?.sort,
    setPanelState,
  };
};